import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import Footer from '../components/Footer';
import Header from '../components/Header';
import SEOHead from '../components/SEO/SEOHead';
import { useBrand } from '../components/BrandProvider';

const FAQPage = () => {
  const [openSections, setOpenSections] = useState({});
  const brand = useBrand();

  const toggleSection = (sectionId) => {
    setOpenSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const faqSections = [
    {
      id: 'couleur',
      title: 'Quelle est la meilleure couleur de toile pour store banne ?',
      image: '/images/faq/echantillons_toiles.png',
      subtitle: 'Comment choisir la bonne couleur ?',
      content: (
        <>
          <p className="mb-4">La couleur que vous choisirez pour votre toile influera sur son confort thermique, son confort visuel, son côté salissant ou pas.</p>
          
          <hr className="my-6 border-gray-300" />
          
          <h4 className="font-bold text-lg mb-2">Confort thermique</h4>
          <p className="mb-2">Le facteur solaire Gtot est le pourcentage d'énergie solaire qui va réellement traverser le store.</p>
          <p className="mb-2">Le Gtot va donc permettre d'évaluer objectivement la performance thermique d'une protection solaire.</p>
          <p className="mb-2">Plus le pourcentage du Gtot est faible, plus la protection solaire est efficace.</p>
          <p className="mb-2">On s'aperçoit que le Gtot varie en fonction de la couleur de la toile de store.</p>
          <p className="mb-4">Par exemple : Une toile noire a un Gtot de 0,05 et une toile blanche un Gtot de 0,18.</p>
          
          <hr className="my-6 border-gray-300" />
          
          <h4 className="font-bold text-lg mb-2">Confort visuel</h4>
          <p className="mb-2">La sensation ressentie sous le store peut aller de l'éblouissement au manque de luminosité, et dépend elle aussi de la couleur de la toile.</p>
          <p className="mb-4">Le confort visuel est très différent selon la couleur de la toile : ★★★ sous une toile noire, et ★☆☆ sous une toile blanche.</p>
          
          <hr className="my-6 border-gray-300" />
          
          <h4 className="font-bold text-lg mb-2">Sensibilité aux salissures</h4>
          <p className="mb-2">Les toiles de couleur claire sont plus sensibles aux salissures en particulier dans les zones urbaines.</p>
          <p className="mb-4">En revanche, les couleurs foncées comme les toiles à rayures, peuvent être préférées pour leur capacité à masquer davantage les salissures.</p>
          
          <hr className="my-6 border-gray-300" />
          
          <div className="bg-brand bg-opacity-10 border-l-4 border-brand p-4 mt-4">
            <p className="font-semibold text-brand">Le conseil du pro :</p>
            <p>Si le confort thermique est votre préoccupation première, choisissez de préférence une toile de couleur sombre.</p>
          </div>
        </>
      )
    },
    {
      id: 'criteres',
      title: 'Critères de Sélection d\'une toile store banne',
      image: '/images/faq/criteres_choix.png',
      subtitle: 'Comment faire le bon choix pour sa toile de store ?',
      content: (
        <>
          <div className="space-y-4">
            <p><strong>La couleur :</strong> C'est en définitive, le critère de choix N°1. Choisissez une couleur qui vous plaise. Si vous souffrez de la chaleur, souvenez vous que les toiles sombres sont plus efficaces thermiquement. (Environ 3 degrés d'écart entre une toile claire et une toile sombre.)</p>
            
            <p><strong>Le grammage</strong> +/- 300g/m² est la norme.</p>
            
            <p><strong>La matière :</strong> 100% acrylique bien sûr, et un fil teinté masse.</p>
            
            <p><strong>La durée de la garantie :</strong> Vous trouverez sur le marché des toiles avec des durées de garantie comprise entre 5 et 10 ans. Les toiles de la <strong>collection {brand.name}</strong> que nous proposons sur notre site, sont quant à elles vendues avec une <strong>garantie de 12 ans</strong>, ce qui est exceptionnel.</p>
            
            <p><strong>Les traitements :</strong> Toutes les toiles de stores bannes en acrylique +/-300g reçoivent des traitements qui les rendent à la fois <strong>étanches</strong>, <strong>imputrescibles</strong> et <strong>autonettoyantes</strong>. Quand vous choisirez votre toile, vérifiez simplement la présence d'un tel traitement.</p>
          </div>
          
          <div className="bg-brand bg-opacity-10 border-l-4 border-brand p-4 mt-6">
            <p className="font-semibold text-brand">Le conseil du pro :</p>
            <p>Demandez la durée de la garantie.</p>
          </div>
        </>
      )
    },
    {
      id: 'mesurer',
      title: 'Comment savoir la taille de sa toile de store ?',
      video: 'y7YIcQe5e58',
      content: (
        <>
          <div className="mb-6">
            <div className="aspect-video">
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/y7YIcQe5e58"
                title="Comment bien mesurer sa toile de store ?"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="rounded-lg"
              ></iframe>
            </div>
          </div>
          
          <h4 className="font-bold text-lg mb-3">Comment bien mesurer sa toile de store ?</h4>
          <p className="mb-4">Dans cette vidéo, découvrez comment mesurer la largeur et l'avancée de votre toile de store.</p>
          
          <ul className="list-disc list-inside space-y-2 mb-4">
            <li>Un mètre ruban métallique est recommandé pour sa précision.</li>
            <li>Il est conseillé de mesurer plusieurs fois pour éviter les erreurs.</li>
            <li>Pour l'avancée, ajouter 30 cm à la mesure pour la toile enroulée.</li>
            <li>La largeur doit être exacte, tandis que l'avancée peut être approximative.</li>
            <li>La commande peut être passée facilement en ligne avec les mesures précises.</li>
            <li>Les mesures du lambrequin sont également nécessaires si vous souhaitez le remplacer.</li>
            <li>Le service client est disponible par chat pour toute assistance.</li>
          </ul>
          
          <hr className="my-6 border-gray-300" />
          
          <div className="bg-brand bg-opacity-10 border-l-4 border-brand p-4">
            <p className="font-semibold text-brand">Le conseil du pro :</p>
            <p>Mesurez plusieurs fois pour éviter les erreurs.</p>
          </div>
        </>
      )
    },
    {
      id: 'changer',
      title: 'Comment changer la toile d\'un store ?',
      video: 'Ox3KxrWDDac',
      content: (
        <>
          <div className="mb-6">
            <div className="aspect-video">
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/Ox3KxrWDDac"
                title="Nouvelle méthode : changer toile store 2024"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="rounded-lg"
              ></iframe>
            </div>
          </div>
          
          <h4 className="font-bold text-lg mb-3">Comment remplacer une toile de store ?</h4>
          <p className="mb-4">Remplacer la toile d'un store banne nécessite peu d'outils mais une bonne organisation. C'est à la portée d'un bricoleur.</p>
          
          <ul className="list-disc list-inside space-y-2 mb-4">
            <li>Elle ne nécessite que très peu d'outillage.</li>
            <li>Il est souvent plus aisé d'être deux.</li>
            <li><strong>Méthodologie :</strong> La méthode présentée est basée sur des années d'expérience, garantissant sécurité et efficacité.</li>
            <li><strong>Travail d'équipe :</strong> Il est recommandé de travailler à plusieurs pour manipuler aisément la toile.</li>
            <li><strong>Nettoyage :</strong> Profitez du démontage pour nettoyer l'armature avec des produits adéquats.</li>
            <li><strong>Astuce :</strong> Une astuce pour économiser du temps lors du remplacement est révélée à la fin de la vidéo.</li>
            <li><strong>Outils nécessaires :</strong> Escabeaux, cordes, tournevis, visseuse, lubrifiant, et éventuellement un nettoyeur haute pression.</li>
            <li><strong>Réinstallation :</strong> L'installation de la nouvelle toile demande précision et patience, surtout au niveau des coutures.</li>
            <li><strong>Mesure :</strong> La toile doit être centrée correctement pour assurer une installation adéquate.</li>
            <li><strong>Option de montage rapide :</strong> Commander une toile avec l'option de montage rapide facilite grandement l'installation.</li>
          </ul>
          
          <hr className="my-6 border-gray-300" />
          
          <div className="bg-brand bg-opacity-10 border-l-4 border-brand p-4">
            <p className="font-semibold text-brand">Le conseil du pro :</p>
            <p>Regardez attentivement les différentes vidéos de notre chaîne YouTube pour préparer vos travaux de remplacement de votre toile de votre store.</p>
          </div>
        </>
      )
    },
    {
      id: 'qui-change',
      title: 'Qui peut changer la toile d\'un store banne ?',
      image: '/images/faq/technicien_installation.png',
      subtitle: 'Un technicien installe une nouvelle toile',
      content: (
        <>
          <p className="mb-4">Changer la toile d'un store est une opération à la portée d'un bon bricoleur.</p>
          <p className="mb-4">C'est donc la seule question à vous poser au préalable : Suis-je bricoleur ?</p>
          <p className="mb-4">Dans l'affirmative, lancez vous ! Il ne faut que très peu d'outillage, surtout de la patience et du soin. (On manipule du tissu)</p>
          <p className="mb-4">Regardez nos vidéos pour vous aider et comprendre la logique des opérations.</p>
          <p className="mb-4">Par ailleurs, si vous le souhaitez, nous pouvons réaliser pour vous le remplacement de votre toile de store. Nous intervenons dans toute la France, ainsi qu'en Suisse.</p>
          <p className="mb-4">En nous confiant ces travaux, vous avez l'assurance de faire confiance à un professionnel spécialisé dans l'installation de stores.</p>
          <p className="mb-4">Nos techniciens possèdent l'expérience, les outils et les connaissances techniques nécessaires pour réaliser cette opération efficacement, tout en respectant les normes de sécurité et en s'assurant que la toile est parfaitement ajustée et fonctionnelle.</p>
          
          <hr className="my-6 border-gray-300" />
          
          <div className="bg-brand bg-opacity-10 border-l-4 border-brand p-4">
            <p className="font-semibold text-brand">Le conseil du pro :</p>
            <p>Regardez nos vidéos, et lancez vous !</p>
          </div>
        </>
      )
    },
    {
      id: 'reparer',
      title: 'Comment réparer une toile de store déchirée ?',
      video: '7xMmUzXtRYw',
      content: (
        <>
          <h4 className="font-bold text-lg mb-3">Toile décousue</h4>
          <p className="mb-2">Vous observez que votre toile se découd car le fil de couture s'est rompu.</p>
          <p className="mb-2">Faites un test : Prenez le fil entre vos doigts et essayez de le casser. Si vous y arrivez aisément, cela signifie qu'il est cuit.</p>
          <p className="mb-2">Votre toile de store va donc se découdre progressivement de partout. Il est dans ce cas illusoire de chercher à la recoudre. Il faudrait en effet refaire l'ensemble des coutures, ce qui n'a aucun sens car cela est plus long et plus cher que de commander une toile de store neuve.</p>
          <p className="mb-4">En revanche, si le fil est toujours résistant, il est parfaitement possible de la réparer.</p>
          
          <hr className="my-6 border-gray-300" />
          
          <h4 className="font-bold text-lg mb-3 mt-6">Toile déchirée</h4>
          
          <div className="mb-6">
            <div className="aspect-video">
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/7xMmUzXtRYw"
                title="Comment réparer facilement une toile de store banne ?"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="rounded-lg"
              ></iframe>
            </div>
          </div>
          
          <p className="mb-2">Vous observez que votre toile s'est déchirée en un endroit précis, et vous en connaissez peut-être la cause.</p>
          <p className="mb-4">Il peut être opportun de la faire réparer.</p>
          <p className="mb-4">Utilisez nos patchs de réparation en vente dans les accessoires : Ils sont spécialement conçus pour cet usage.</p>
          
          <hr className="my-6 border-gray-300" />
          
          <div className="bg-brand bg-opacity-10 border-l-4 border-brand p-4">
            <p className="font-semibold text-brand">Le conseil du pro :</p>
            <p>Même si le tissu est encore tout bon, neuf fois sur dix : Toile décousue = toile foutue !</p>
          </div>
        </>
      )
    },
    {
      id: 'entretien',
      title: 'Entretien et maintenance des toiles de stores bannes',
      image: '/images/faq/produits_entretien.png',
      subtitle: 'Comment entretenir votre toile de store ?',
      content: (
        <>
          <p className="mb-4">Prolongez la durée de vie de votre toile de store de store banne en suivant les quelques conseils suivants :</p>
          
          <ul className="list-disc list-inside space-y-2 mb-4">
            <li>N'exposez pas votre toile aux intempéries, et repliez le store quand il pleut.</li>
            <li>Si votre toile s'est malencontreusement mouillée, faites la sécher dès que possible.</li>
            <li>Nettoyez les fientes d'oiseau le plus vite possible en utilisant de l'eau savonneuse tiède, et une brosse douce. Après il sera trop tard, et il subsistera une trace.</li>
            <li>Nettoyez votre toile au moins tous les 6 mois pour enlever poussières et pollution.</li>
            <li>Renouvelez le traitement d'étanchéité une fois par an en utilisant le produit imperméabilisant en vente sur notre site.</li>
          </ul>
          
          <hr className="my-6 border-gray-300" />
          
          <div className="bg-brand bg-opacity-10 border-l-4 border-brand p-4">
            <p className="font-semibold text-brand">Le conseil du pro :</p>
            <p>Évitez absolument les toiles de couleur claires blanches et écrues, beaucoup trop salissantes.</p>
          </div>
        </>
      )
    },
    {
      id: 'personnalisation',
      title: 'Personnalisation de votre toile store banne',
      image: '/images/faq/soudure_hyperfrequence.png',
      subtitle: 'Le rendu incomparable de la soudure hyperfréquence',
      content: (
        <>
          <p className="mb-4">Dans notre usine spécialisée d'Aix en Provence, nous fabriquons vos toiles de stores bannes, exactement à l'identique de celles que vous avez déjà.</p>
          <p className="mb-4">Les mesures sont les mêmes, et vous pourrez choisir un coloris au choix parmi les 2000 coloris que nous vous proposons.</p>
          <p className="mb-4">Vous pouvez choisir toutes les formes et dimensions pour vos lambrequins.</p>
          <p className="mb-4">Les finitions et systèmes d'accroche (joncs, agrafes, fourreaux …) sont personnalisables à l'infini pour vous permettre une installation facile.</p>
          <p className="mb-4">Mais surtout, nous sommes les seuls à vous proposer des finitions de haut de gamme comme la <strong>soudure hyperfréquence</strong>, le jonc magnétique ou encore le fil Tenara™</p>
        </>
      )
    },
    {
      id: 'fournisseurs',
      title: 'Fournisseurs de tissu pour stores bannes',
      content: (
        <>
          <p className="mb-4">Sur le marché Européen on rencontre des tissus en provenance des pays suivants :</p>
          
          <div className="space-y-3">
            <p><strong>France :</strong> {brand.name} - DICKSON - LATIM - Serge FERRARI</p>
            <p><strong>Espagne :</strong> RECASENS - SAULEDA - CITEL</p>
            <p><strong>Autriche :</strong> SATTLER</p>
            <p><strong>Italie :</strong> GIOVANARDI - PARA</p>
          </div>
          
          <p className="mt-4 mb-4">Choisir un tissu parmi l'une de ces grandes marques est pour vous l'assurance de choisir un produit de qualité.</p>
          <p className="mb-4">Même s'il existe des différences subtiles de qualité d'une marque à l'autre, celles-ci sont imperceptibles et restent une affaire de spécialiste.</p>
          <p className="mb-4">Quel que soit le tissu choisi, s'il est de l'un de ces marques, vous pouvez avoir l'assurance de choisir un produit de qualité, tissé dans les règles de l'art à partir de fibre acrylique teintée masse, et garanti dans le temps.</p>
          <p className="mb-4">Bien sûr, toutes ces marques sont disponibles sur notre site. Faites votre devis immédiat sur cette page.</p>
        </>
      )
    },
    {
      id: 'tendances',
      title: 'Tendances et innovations des toiles de stores Bannes',
      image: '/images/faq/toile_photovoltaique.png',
      subtitle: 'Le futur est-il aux toiles de stores photovoltaïques ?',
      content: (
        <>
          <p className="mb-4"><strong>La tendance est aux unis</strong> par rapport à des rayures classiques et fantaisies qui semblent bien désormais démodées. Les coloris <strong>gris et beiges</strong> remportent aujourd'hui un très grand succès.</p>
          <p className="mb-4">L'acrylique est fabriqué à 100% à base de pétrole. La pénurie annoncée devrait faire apparaître des alternatives à cette production à partir de ressources fossiles. Les fabricants de tissu travaillent déjà à l'incorporation de fibres et matières recyclées dans leur production. Par ailleurs d'autres fibres comme le lin ou le coton pourraient faire leur grand retour avec l'aide de technologies permettant de gommer leur principal inconvénient qu'est la décoloration rapide.</p>
          <p className="mb-4">Nous ne serions pas surpris que les toiles de stores bannes deviennent plus technologiques et intègrent la production d'énergie électrique solaire ou des capacités de communication et de refroidissement connectées.</p>
        </>
      )
    },
    {
      id: 'conclusion',
      title: 'Conclusion',
      content: (
        <>
          <p className="mb-4">En conclusion, choisissez une toile store banne dont la couleur vous plaît, si vous hésitez entre deux teintes, choisissez la plus sombre. Vérifiez bien qu'il s'agit d'une toile en acrylique et que son grammage est proche de 300g/m².</p>
          <p className="mb-4">Les 2000 tissus que nous proposons sur notre site remplissent bien sûr ces caractéristiques. Faites votre devis en ligne et commandez en quelques secondes tout simplement sur cette page.</p>
        </>
      )
    }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <SEOHead 
        title="FAQ - Questions Fréquentes sur les Toiles de Stores Bannes"
        description="Retrouvez toutes les réponses à vos questions sur les toiles de stores bannes : choix de couleur, installation, entretien, réparation, mesures et fournisseurs. Conseils d'experts."
        url="/faq"
        keywords={['faq toile store banne', 'questions store banne', 'comment choisir toile store', 'entretien toile store', 'réparer toile store']}
      />
      
      <Header />

      {/* Main Content */}
      <main className="flex-grow">
        <div className="max-w-5xl mx-auto px-4 py-12">
          {/* Page Title */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-brand mb-4">FAQ - Questions fréquentes</h1>
            <p className="text-xl text-gray-600">{brand.name}</p>
            <div className="flex justify-center mt-6">
              <img src="/images/faq/faq_icon.png" alt="FAQ" className="h-20" />
            </div>
            <p className="text-gray-600 mt-4 italic">* "Frequently Asked Questions" ou Foire Aux Questions en français.</p>
            <p className="text-gray-700 mt-2">Si vous souhaitez approfondir certains aspects des toiles de stores bannes, évoqués ici, retrouvez ci-dessous les réponses aux questions fréquemment posées :</p>
          </div>

          {/* FAQ Accordion */}
          <div className="space-y-4">
            {faqSections.map((section) => (
              <div key={section.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                {/* Section Header - Clickable */}
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
                >
                  <h3 className="text-xl font-bold text-brand pr-4">{section.title}</h3>
                  {openSections[section.id] ? (
                    <ChevronUp className="w-6 h-6 text-brand flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-6 h-6 text-brand flex-shrink-0" />
                  )}
                </button>

                {/* Section Content - Collapsible */}
                {openSections[section.id] && (
                  <div className="px-6 py-6 border-t border-gray-200">
                    {section.image && (
                      <div className="mb-6">
                        <img 
                          src={section.image} 
                          alt={section.title}
                          className="w-full max-w-2xl mx-auto rounded-lg shadow-sm"
                        />
                        {section.subtitle && (
                          <p className="text-center text-sm text-gray-600 mt-2 italic">{section.subtitle}</p>
                        )}
                      </div>
                    )}
                    <div className="prose max-w-none text-gray-700">
                      {section.content}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default FAQPage;